import {
  ModuleId,
  Question,
  QuestionState,
  SubSkillProgress,
  DifficultyLevel,
} from '../types'
import {
  ALL_QUESTIONS_CORE,
  getQuestionWithExplanation,
} from '../data/questionLoader'
import { GroupId } from '../config/systemConfig'
import {
  getLevelById,
  getLevelsByGroup,
  getAllLevelsOrdered,
  LevelConfig,
} from '../config/levels'

/**
 * ADAPTIVE ENGINE v8.1 — Strict Difficulty Window
 *
 * Core philosophy: Per-subSkill progression along a CONTINUOUS difficulty ladder
 * with a STRICT difficulty window around each subSkill's mastery position.
 *
 * v8.2 — Family-aware selection
 * - NEW: Questions are grouped by familyId (shared across difficulty levels)
 * - NEW: Family mastery is computed by aggregating question states per familyId
 * - NEW: Selection prioritizes families with lower mastery (less practiced = higher priority)
 * - IMPROVED: Session deduplication by familyId now effective with shared family IDs
 * - Session invariant: max 1 question per familyId per session (prevents concept repetition)
 *
 * v8.1 — Critical fix: Strict difficulty window enforcement
 * - FIXED: Empty levels no longer cascade-unlock the entire ladder
 * - NEW: Each subSkill has a mastery position (highest unlocked level with content)
 * - NEW: Questions are ONLY selected within a tight window around mastery position
 * - Window: [masteryPosition - 1 step, masteryPosition + 1 step] on the ladder
 * - This prevents M6 students from seeing B8/E8 questions
 *
 * Preserved from v8.0:
 * - Cross-grade progression (E6+ can unlock B7, etc.)
 * - Per-subSkill independent progression
 * - Anchor grade as starting point, not boundary
 * - Mastery requirements and fast-track detection
 * - Fallback detection for struggling students
 * - Session generation with familyId uniqueness
 *
 * ============================================================
 * 🔄 MIGRATION NOTE: difficultyTier Integration (Planned)
 * ============================================================
 *
 * The adaptive engine currently uses the `difficulty` field (level IDs: 101-306)
 * for all progression logic. The new `difficultyTier` field (continuous 1-18 scale)
 * is now available on all questions via auto-derivation in questionLoader.ts.
 *
 * CURRENT STATE:
 * - difficulty (101-306) remains the primary field for all engine logic
 * - difficultyTier (1-18) is available but NOT yet used by the engine
 * - familyId is used for family-aware selection (v8.2)
 *
 * FUTURE INTEGRATION (after JSON migration is complete):
 * - Engine can use difficultyTier for more granular within-family progression
 * - Family mastery can track per-tier mastery: { tier 1: mastered, tier 3: partial, ... }
 * - conceptId can enable cross-family concept clustering
 * - variantIndex can improve anti-repetition logic
 *
 * The difficulty field and difficultyTier are fully consistent (derived from
 * the same source), so the engine can be migrated incrementally.
 *
 * See: src/docs/FAMILY_ID_MIGRATION.md for full specification
 * See: src/utils/difficultyTierMapping.ts for tier ↔ difficulty mapping
 * ============================================================
 */

// Configuration
const CONFIG = {
  /** Score needed to consider subSkill "mastered" */
  MASTERY_THRESHOLD: 85,
  /** Score below this is "struggling" */
  STRUGGLE_THRESHOLD: 35,
  /** Number of recent attempts to weigh heavily in subSkill mastery */
  RECENT_WINDOW: 8,
  /** Minimum questions answered before subSkill mastery is considered reliable */
  MIN_QUESTIONS_FOR_RELIABLE_MASTERY: 5,
  /** Maximum questions from mastered pool per session (for review) */
  MAX_MASTERED_PER_SESSION: 2,
  /** Number of recently-answered questions to deprioritize (cross-session cooldown) */
  COOLDOWN_SIZE: 20,

  // === LEVEL PROGRESSION ===
  /** Ratio of practiced/mastered questions needed to unlock next level */
  LEVEL_UNLOCK_THRESHOLD: 0.5,
  /** Minimum questions attempted at a level before unlock is possible */
  LEVEL_UNLOCK_MIN_ATTEMPTED: 1,
  /** Consecutive wrong answers at current level to trigger fallback */
  FALLBACK_CONSECUTIVE_WRONG: 3,
  /** Consecutive correct answers at a level to fast-track unlock next level */
  FAST_TRACK_CONSECUTIVE_CORRECT: 3,

  // === DIFFICULTY WINDOW ===
  /**
   * Number of ladder steps BELOW mastery position to include in the window.
   * 1 = include one level below for reinforcement.
   */
  DIFFICULTY_WINDOW_BELOW: 1,
  /**
   * Number of ladder steps ABOVE mastery position to include in the window.
   * 1 = include one level above for advancement testing.
   */
  DIFFICULTY_WINDOW_ABOVE: 1,

  // === SESSION GENERATION ===
  /** Max retries per question slot when familyId collisions occur */
  MAX_RETRIES_PER_SLOT: 5,
  /** Max total retries across entire session generation */
  MAX_TOTAL_RETRIES: 50,
}

/**
 * Fisher-Yates shuffle for proper randomization
 */
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

// ============================================================
// CROSS-GRADE LADDER HELPERS
// ============================================================

/** Cache the full ordered ladder */
const FULL_LADDER: LevelConfig[] = getAllLevelsOrdered()

/** Map from level ID to ladder index for O(1) lookup */
const LEVEL_INDEX_MAP: Map<number, number> = new Map(
  FULL_LADDER.map((l, i) => [l.id, i]),
)

/**
 * Get the ladder index of a level ID. Returns -1 if not found.
 */
function getLadderIndex(levelId: number): number {
  return LEVEL_INDEX_MAP.get(levelId) ?? -1
}

/**
 * Compute the allowed difficulty window (set of level IDs) for a given
 * mastery position on the ladder.
 *
 * Window = [masteryIndex - WINDOW_BELOW, masteryIndex + WINDOW_ABOVE]
 * Clamped to ladder bounds AND to the starting level floor.
 *
 * v8.2: The window never goes below (startingLevelIndex - WINDOW_BELOW).
 * This prevents B6 questions from appearing when the student starts at M6,
 * even for subSkills whose mastery position is at B6.
 */
function getDifficultyWindow(
  masteryLevelId: number,
  startingLevelId?: number,
): Set<number> {
  const centerIdx = getLadderIndex(masteryLevelId)
  if (centerIdx === -1) return new Set([masteryLevelId])

  // Compute the absolute floor: never go below (startingLevel - WINDOW_BELOW)
  let floorIdx = 0
  if (startingLevelId !== undefined) {
    const startIdx = getLadderIndex(startingLevelId)
    if (startIdx !== -1) {
      floorIdx = Math.max(0, startIdx - CONFIG.DIFFICULTY_WINDOW_BELOW)
    }
  }

  const windowIds = new Set<number>()
  const minIdx = Math.max(floorIdx, centerIdx - CONFIG.DIFFICULTY_WINDOW_BELOW)
  const maxIdx = Math.min(
    FULL_LADDER.length - 1,
    centerIdx + CONFIG.DIFFICULTY_WINDOW_ABOVE,
  )

  for (let i = minIdx; i <= maxIdx; i++) {
    windowIds.add(FULL_LADDER[i].id)
  }

  return windowIds
}

// ============================================================
// LEVEL PROGRESSION SYSTEM (CROSS-GRADE)
// ============================================================

function detectFastTrack(
  moduleId: ModuleId,
  levelId: number,
  questionStates: Record<string, QuestionState>,
): boolean {
  const questionsAtLevel = ALL_QUESTIONS_CORE.filter(
    (q) => q.module === moduleId && q.difficulty === levelId,
  )

  const allAttempts: {
    isCorrect: boolean
    timestamp: number
    subSkill?: string
  }[] = []
  questionsAtLevel.forEach((q) => {
    const state = questionStates[q.id]
    if (state && state.history.length > 0) {
      state.history.forEach((h) => {
        allAttempts.push({ ...h, subSkill: q.subSkill })
      })
    }
  })

  if (allAttempts.length < CONFIG.FAST_TRACK_CONSECUTIVE_CORRECT) return false

  allAttempts.sort((a, b) => b.timestamp - a.timestamp)

  const recentAttempts = allAttempts.slice(
    0,
    CONFIG.FAST_TRACK_CONSECUTIVE_CORRECT,
  )
  const allCorrect = recentAttempts.every((a) => a.isCorrect)
  if (!allCorrect) return false

  const uniqueSubSkills = new Set(
    recentAttempts.map((a) => a.subSkill).filter(Boolean),
  )
  if (uniqueSubSkills.size < 2) {
    const levelSubSkills = new Set(
      questionsAtLevel.map((q) => q.subSkill).filter(Boolean),
    )
    if (levelSubSkills.size >= 2) {
      return false
    }
  }

  return true
}

/**
 * Determine which levels are unlocked for a specific subSkill
 * along the FULL difficulty ladder (cross-grade).
 *
 * KEY INVARIANTS (v8.1):
 * 1. The starting level is the FLOOR — all levels from the beginning of the
 *    ladder up to (and including) the starting level are unlocked by default.
 * 2. Levels ABOVE the starting level must be earned through progression.
 * 3. Progression crosses group boundaries seamlessly (E6+ → B7, E7+ → B8).
 * 4. Empty levels (no questions for this subSkill) do NOT cascade-unlock.
 *    They are simply skipped — the student must earn progression at levels
 *    that DO have content.
 * 5. Fallback can temporarily remove the highest unlocked level.
 * 6. Never falls back below the starting level.
 */
function getUnlockedLevelsForSubSkill(
  moduleId: ModuleId,
  subSkill: string,
  questionStates: Record<string, QuestionState>,
  startingLevelId: number,
  fastTrackedLevels: Set<number>,
): Set<number> {
  const ladder = FULL_LADDER
  const unlocked = new Set<number>()

  if (ladder.length === 0) return unlocked

  // Phase 1: Unlock all levels from ladder start up to (and including) the starting level.
  for (const level of ladder) {
    unlocked.add(level.id)
    if (level.id >= startingLevelId) break
  }
  const startingLevelExists = ladder.some((l) => l.id === startingLevelId)
  if (startingLevelExists) {
    unlocked.add(startingLevelId)
  }

  // Phase 2: Check progression for levels at and above the starting level.
  // Each level with content must be earned. Empty levels are skipped but do NOT
  // auto-unlock the next level.
  const startingIndex = ladder.findIndex((l) => l.id >= startingLevelId)
  if (startingIndex === -1) return unlocked

  for (let i = startingIndex; i < ladder.length; i++) {
    const level = ladder[i]

    // If this level isn't unlocked yet, stop
    if (!unlocked.has(level.id)) break

    // Fast-track: if this level was fast-tracked, unlock the next one immediately
    if (fastTrackedLevels.has(level.id)) {
      if (i + 1 < ladder.length) {
        unlocked.add(ladder[i + 1].id)
        continue
      }
    }

    // Find questions for this subSkill at this level
    const questionsAtLevel = ALL_QUESTIONS_CORE.filter(
      (q) =>
        q.module === moduleId &&
        q.subSkill === subSkill &&
        q.difficulty === level.id,
    )

    // CRITICAL FIX (v8.1): Empty levels do NOT cascade-unlock.
    // If there are no questions for this subSkill at this level,
    // just skip it. Do NOT unlock the next level automatically.
    // The student must demonstrate competence at levels that have content.
    if (questionsAtLevel.length === 0) {
      continue
    }

    // Check progression: has the student demonstrated enough competence?
    let practicedOrMastered = 0
    let totalAttempted = 0
    questionsAtLevel.forEach((q) => {
      const state = questionStates[q.id]
      if (state && state.attempts > 0) {
        totalAttempted++
        if (state.status === 'practiced' || state.status === 'mastered') {
          practicedOrMastered++
        }
      }
    })

    const ratio = practicedOrMastered / questionsAtLevel.length

    if (
      ratio >= CONFIG.LEVEL_UNLOCK_THRESHOLD &&
      totalAttempted >= CONFIG.LEVEL_UNLOCK_MIN_ATTEMPTED
    ) {
      // Unlock the next level on the ladder (crosses group boundaries)
      if (i + 1 < ladder.length) {
        unlocked.add(ladder[i + 1].id)
      }
    }
  }

  // Phase 3: Fallback detection
  const highestUnlockedLevel = getHighestUnlockedLevel(unlocked, ladder)
  if (highestUnlockedLevel && unlocked.size > 1) {
    if (highestUnlockedLevel.id > startingLevelId) {
      const shouldFallback = detectFallback(
        moduleId,
        subSkill,
        highestUnlockedLevel.id,
        questionStates,
      )
      if (shouldFallback) {
        unlocked.delete(highestUnlockedLevel.id)
        console.log(
          `⬇️ Fallback: ${subSkill} dropped from ${highestUnlockedLevel.label} (${CONFIG.FALLBACK_CONSECUTIVE_WRONG}+ consecutive wrong)`,
        )
      }
    }
  }

  return unlocked
}

function getHighestUnlockedLevel(
  unlocked: Set<number>,
  ladder: LevelConfig[],
): LevelConfig | null {
  for (let i = ladder.length - 1; i >= 0; i--) {
    if (unlocked.has(ladder[i].id)) {
      return ladder[i]
    }
  }
  return null
}

function detectFallback(
  moduleId: ModuleId,
  subSkill: string,
  levelId: number,
  questionStates: Record<string, QuestionState>,
): boolean {
  const questionsAtLevel = ALL_QUESTIONS_CORE.filter(
    (q) =>
      q.module === moduleId &&
      q.subSkill === subSkill &&
      q.difficulty === levelId,
  )

  const allAttempts: { isCorrect: boolean; timestamp: number }[] = []
  questionsAtLevel.forEach((q) => {
    const state = questionStates[q.id]
    if (state && state.history.length > 0) {
      allAttempts.push(...state.history)
    }
  })

  if (allAttempts.length < CONFIG.FALLBACK_CONSECUTIVE_WRONG) return false

  allAttempts.sort((a, b) => b.timestamp - a.timestamp)

  const recentAttempts = allAttempts.slice(0, CONFIG.FALLBACK_CONSECUTIVE_WRONG)
  return recentAttempts.every((a) => !a.isCorrect)
}

/**
 * Compute unlocked levels for ALL subSkills in a module across the FULL ladder.
 *
 * Also performs bounded global propagation: if a subSkill has questions
 * at a level that is globally unlocked (by any other subSkill), the
 * LOWEST such level is added to that subSkill's unlocked set.
 */
function computeAllUnlockedLevels(
  moduleId: ModuleId,
  questionStates: Record<string, QuestionState>,
  startingLevelId: number,
): Map<string, Set<number>> {
  const ladder = FULL_LADDER

  // Discover all subSkills that have questions in this module
  const subSkills = new Set<string>()
  ALL_QUESTIONS_CORE.forEach((q) => {
    if (q.module === moduleId && q.subSkill) {
      subSkills.add(q.subSkill)
    }
  })

  // Detect fast-tracked levels (module-wide)
  const fastTrackedLevels = new Set<number>()
  for (const level of ladder) {
    if (detectFastTrack(moduleId, level.id, questionStates)) {
      fastTrackedLevels.add(level.id)
    }
  }

  // Compute per-subSkill unlocked levels
  const result = new Map<string, Set<number>>()
  subSkills.forEach((subSkill) => {
    result.set(
      subSkill,
      getUnlockedLevelsForSubSkill(
        moduleId,
        subSkill,
        questionStates,
        startingLevelId,
        fastTrackedLevels,
      ),
    )
  })

  // Build global unlocked set (union of all subSkill unlocked levels)
  const globalUnlocked = new Set<number>()
  result.forEach((levels) => {
    levels.forEach((levelId) => globalUnlocked.add(levelId))
  })

  // Bounded global propagation: for each subSkill, ensure it has access
  // to at least its LOWEST level with questions, IF that level is globally unlocked.
  subSkills.forEach((subSkill) => {
    const unlockedLevels = result.get(subSkill)!

    let lowestLevelWithQuestions: number | null = null
    for (const level of ladder) {
      const hasQuestions = ALL_QUESTIONS_CORE.some(
        (q) =>
          q.module === moduleId &&
          q.subSkill === subSkill &&
          q.difficulty === level.id,
      )
      if (hasQuestions) {
        lowestLevelWithQuestions = level.id
        break
      }
    }

    if (lowestLevelWithQuestions === null) return

    if (
      !unlockedLevels.has(lowestLevelWithQuestions) &&
      globalUnlocked.has(lowestLevelWithQuestions)
    ) {
      unlockedLevels.add(lowestLevelWithQuestions)
    }
  })

  return result
}

/**
 * For a given subSkill, find its "mastery position" — the highest unlocked level
 * that actually has questions for this subSkill.
 *
 * This is the center of the difficulty window for question selection.
 */
function getMasteryPosition(
  moduleId: ModuleId,
  subSkill: string,
  unlockedLevels: Set<number>,
): number | null {
  let highestWithContent: number | null = null

  for (let i = FULL_LADDER.length - 1; i >= 0; i--) {
    const level = FULL_LADDER[i]
    if (!unlockedLevels.has(level.id)) continue

    const hasQuestions = ALL_QUESTIONS_CORE.some(
      (q) =>
        q.module === moduleId &&
        q.subSkill === subSkill &&
        q.difficulty === level.id,
    )
    if (hasQuestions) {
      highestWithContent = level.id
      break
    }
  }

  return highestWithContent
}

/**
 * Check if a specific question is unlocked based on the per-subSkill unlock map.
 */
function isQuestionUnlocked(
  question: { subSkill?: string; difficulty: number },
  unlockedBySubSkill: Map<string, Set<number>>,
  globalUnlockedLevels: Set<number>,
): boolean {
  if (!question.subSkill) {
    return globalUnlockedLevels.has(question.difficulty)
  }

  const unlockedLevels = unlockedBySubSkill.get(question.subSkill)
  if (!unlockedLevels) {
    return false
  }

  return unlockedLevels.has(question.difficulty)
}

// ============================================================
// FAMILY MASTERY SYSTEM (v8.2)
// ============================================================

/**
 * Compute mastery per familyId by aggregating question states.
 *
 * For each family, we look at all questions that share that familyId
 * (optionally filtered to a difficulty window) and compute:
 * - How many questions exist in the family
 * - How many have been attempted
 * - A weighted mastery score (0-100) based on recent performance
 *
 * This enables family-aware question selection: families with lower
 * mastery get higher priority, ensuring balanced concept coverage.
 */
function computeFamilyMastery(
  moduleId: ModuleId,
  questionStates: Record<string, QuestionState>,
  allowedQuestionIds?: Set<string>,
): Map<
  string,
  { mastery: number; totalQuestions: number; attemptedCount: number }
> {
  const familyMap = new Map<
    string,
    { mastery: number; totalQuestions: number; attemptedCount: number }
  >()

  // Group questions by familyId
  const questionsByFamily = new Map<string, typeof ALL_QUESTIONS_CORE>()
  ALL_QUESTIONS_CORE.forEach((q) => {
    if (q.module !== moduleId || !q.familyId) return
    if (allowedQuestionIds && !allowedQuestionIds.has(q.id)) return

    if (!questionsByFamily.has(q.familyId)) {
      questionsByFamily.set(q.familyId, [])
    }
    questionsByFamily.get(q.familyId)!.push(q)
  })

  // Compute mastery per family
  questionsByFamily.forEach((questions, familyId) => {
    let totalCorrect = 0
    let totalAttempts = 0
    let attemptedCount = 0

    questions.forEach((q) => {
      const state = questionStates[q.id]
      if (state && state.attempts > 0) {
        attemptedCount++
        totalAttempts += state.attempts
        totalCorrect += state.correctAttempts
      }
    })

    const mastery =
      totalAttempts > 0 ? Math.round((totalCorrect / totalAttempts) * 100) : 0

    familyMap.set(familyId, {
      mastery,
      totalQuestions: questions.length,
      attemptedCount,
    })
  })

  return familyMap
}

/**
 * Get the family priority score for a question.
 * Lower mastery families get HIGHER priority scores (more likely to be selected).
 * Unattempted families get the highest priority.
 */
function getFamilyPriorityScore(
  familyId: string | undefined,
  familyMastery: Map<
    string,
    { mastery: number; totalQuestions: number; attemptedCount: number }
  >,
): number {
  if (!familyId) return 50 // Neutral priority for questions without familyId

  const family = familyMastery.get(familyId)
  if (!family) return 50

  // Unattempted families get highest priority
  if (family.attemptedCount === 0) return 80

  // Struggling families (low mastery) get high priority
  if (family.mastery < 40) return 90
  if (family.mastery < 60) return 70
  if (family.mastery < 80) return 50

  // Well-mastered families get low priority (but not zero — review is still valuable)
  return 20
}

// ============================================================
// END FAMILY MASTERY SYSTEM
// ============================================================

// ============================================================
// END LEVEL PROGRESSION SYSTEM
// ============================================================

export function computeSubSkillMastery(
  moduleId: ModuleId,
  subSkill: string,
  questionStates: Record<string, QuestionState>,
  group?: GroupId,
): SubSkillProgress {
  const relevantQuestions = ALL_QUESTIONS_CORE.filter((q) => {
    if (q.module !== moduleId || q.subSkill !== subSkill) return false
    if (group !== undefined) {
      const level = getLevelById(q.difficulty)
      if (!level || level.group !== group) return false
    }
    return true
  })

  if (relevantQuestions.length === 0) {
    return {
      subSkill,
      masteryScore: 0,
      questionsAnswered: 0,
      correctCount: 0,
      trend: 'stable',
      lastPracticed: 0,
    }
  }

  let totalAttempts = 0
  let correctAttempts = 0
  let lastPracticed = 0

  const allAttempts: { isCorrect: boolean; timestamp: number }[] = []

  relevantQuestions.forEach((q) => {
    const state = questionStates[q.id]
    if (state && state.attempts > 0) {
      totalAttempts += state.attempts
      correctAttempts += state.correctAttempts
      lastPracticed = Math.max(lastPracticed, state.lastAttemptDate)
      allAttempts.push(...state.history)
    }
  })

  allAttempts.sort((a, b) => b.timestamp - a.timestamp)

  const recentWindow = allAttempts.slice(0, CONFIG.RECENT_WINDOW)

  if (recentWindow.length === 0) {
    return {
      subSkill,
      masteryScore: 0,
      questionsAnswered: 0,
      correctCount: 0,
      trend: 'stable',
      lastPracticed: 0,
    }
  }

  let weightedScore = 0
  let totalWeight = 0

  recentWindow.forEach((attempt, index) => {
    const weight = 1 / (index + 1)
    weightedScore += (attempt.isCorrect ? 100 : 0) * weight
    totalWeight += weight
  })

  let finalScore = Math.round(weightedScore / totalWeight)

  if (totalAttempts < CONFIG.MIN_QUESTIONS_FOR_RELIABLE_MASTERY) {
    const volumeMultiplier =
      totalAttempts / CONFIG.MIN_QUESTIONS_FOR_RELIABLE_MASTERY
    finalScore = Math.round(finalScore * volumeMultiplier)
  }

  let trend: 'improving' | 'stable' | 'struggling' = 'stable'
  if (recentWindow.length >= 4) {
    const recentHalf = recentWindow.slice(
      0,
      Math.floor(recentWindow.length / 2),
    )
    const olderHalf = recentWindow.slice(Math.floor(recentWindow.length / 2))

    const recentAvg =
      recentHalf.filter((a) => a.isCorrect).length / recentHalf.length
    const olderAvg =
      olderHalf.filter((a) => a.isCorrect).length / olderHalf.length

    if (recentAvg > olderAvg + 0.2) trend = 'improving'
    else if (recentAvg < olderAvg - 0.2) trend = 'struggling'
  }

  return {
    subSkill,
    masteryScore: finalScore,
    questionsAnswered: totalAttempts,
    correctCount: correctAttempts,
    trend,
    lastPracticed,
  }
}

/**
 * Select a single question from the difficulty ladder with STRICT window enforcement.
 *
 * v8.1 INVARIANTS:
 * - Computes mastery position per subSkill (highest unlocked level with content)
 * - Builds a difficulty window of ±1 step around each subSkill's mastery position
 * - ONLY questions within the difficulty window are eligible for selection
 * - This prevents distant-level jumps (e.g., M6 student seeing B8 questions)
 * - Within the window, standard adaptive selection applies (balancing, cooldown, etc.)
 */
function selectFromLadder(
  moduleId: ModuleId,
  questionStates: Record<string, QuestionState>,
  subSkillProgress: Record<string, SubSkillProgress>,
  recentQuestionIds: string[],
  sessionMasteredCount: number,
  recentlyAnsweredIds: string[] = [],
  startingLevelId: number,
): Question | null {
  const unlockedBySubSkill = computeAllUnlockedLevels(
    moduleId,
    questionStates,
    startingLevelId,
  )

  // Build global unlocked set for questions without subSkill
  const globalUnlockedLevels = new Set<number>()
  unlockedBySubSkill.forEach((levels) => {
    levels.forEach((levelId) => globalUnlockedLevels.add(levelId))
  })

  // === STRICT DIFFICULTY WINDOW (v8.1) ===
  // For each subSkill, compute mastery position and allowed difficulty window.
  const windowBySubSkill = new Map<string, Set<number>>()
  const masteryPositionBySubSkill = new Map<string, number>()

  unlockedBySubSkill.forEach((unlockedLevels, subSkill) => {
    const masteryPos = getMasteryPosition(moduleId, subSkill, unlockedLevels)
    if (masteryPos !== null) {
      masteryPositionBySubSkill.set(subSkill, masteryPos)
      windowBySubSkill.set(
        subSkill,
        getDifficultyWindow(masteryPos, startingLevelId),
      )
    }
  })

  // Build a global difficulty window (union of all subSkill windows)
  // Used for questions without a subSkill
  const globalDifficultyWindow = new Set<number>()
  windowBySubSkill.forEach((window) => {
    window.forEach((levelId) => globalDifficultyWindow.add(levelId))
  })

  // Filter: module match + unlocked + within difficulty window
  const allAvailableQuestions = ALL_QUESTIONS_CORE.filter((q) => {
    if (q.module !== moduleId) return false

    // Must be unlocked
    if (!isQuestionUnlocked(q, unlockedBySubSkill, globalUnlockedLevels))
      return false

    // Must be within the difficulty window
    if (q.subSkill) {
      const window = windowBySubSkill.get(q.subSkill)
      if (!window) return false
      return window.has(q.difficulty)
    } else {
      // Questions without subSkill use the global window
      return globalDifficultyWindow.has(q.difficulty)
    }
  })

  if (allAvailableQuestions.length === 0) return null

  // Log window info for debugging
  if (allAvailableQuestions.length > 0) {
    const levels = new Set(allAvailableQuestions.map((q) => q.difficulty))
    const levelLabels = Array.from(levels)
      .sort((a, b) => a - b)
      .map((id) => getLevelById(id)?.label || `${id}`)
    console.log(
      `🎯 Difficulty window: ${levelLabels.join(', ')} (${allAvailableQuestions.length} questions)`,
    )
  }

  // Remove questions already in this session
  const recentSet = new Set(recentQuestionIds)
  const availableQuestions = allAvailableQuestions.filter(
    (q) => !recentSet.has(q.id),
  )

  if (availableQuestions.length === 0) {
    // All questions used in session — try non-mastered ones as fallback
    const nonMastered = allAvailableQuestions.filter((q) => {
      const state = questionStates[q.id]
      return !state || state.status !== 'mastered'
    })
    if (nonMastered.length > 0) {
      const shuffled = shuffleArray(nonMastered)
      return getQuestionWithExplanation(shuffled[0].id)
    }
    return null
  }

  // Apply cross-session cooldown
  const cooldownSet = new Set(
    recentlyAnsweredIds.slice(0, CONFIG.COOLDOWN_SIZE),
  )
  const freshQuestions = availableQuestions.filter(
    (q) => !cooldownSet.has(q.id),
  )
  const cooldownQuestions = availableQuestions.filter((q) =>
    cooldownSet.has(q.id),
  )

  const primaryPool =
    freshQuestions.length > 0 ? freshQuestions : cooldownQuestions

  // === MASTERED CAP ENFORCEMENT ===
  let filteredPool = primaryPool
  if (sessionMasteredCount >= CONFIG.MAX_MASTERED_PER_SESSION) {
    const nonMasteredPool = primaryPool.filter((q) => {
      const state = questionStates[q.id]
      return !state || state.status !== 'mastered'
    })
    if (nonMasteredPool.length > 0) {
      filteredPool = nonMasteredPool
    }
  }

  // === PRIORITIZATION ===
  // Within the (now tight) window, prioritize mastery-position questions
  // v8.2: Also factor in family mastery for balanced concept coverage
  const allowedIds = new Set(filteredPool.map((q) => q.id))
  const familyMastery = computeFamilyMastery(
    moduleId,
    questionStates,
    allowedIds,
  )

  const prioritizedPool = [...filteredPool].sort((a, b) => {
    const masteryA = masteryPositionBySubSkill.get(a.subSkill || '') || 0
    const masteryB = masteryPositionBySubSkill.get(b.subSkill || '') || 0

    const isAtMasteryA = a.difficulty === masteryA
    const isAtMasteryB = b.difficulty === masteryB

    // Mastery-position questions first
    if (isAtMasteryA && !isAtMasteryB) return -1
    if (!isAtMasteryA && isAtMasteryB) return 1

    // v8.2: Family mastery tiebreaker — prefer less-practiced families
    const familyPriorityA = getFamilyPriorityScore(a.familyId, familyMastery)
    const familyPriorityB = getFamilyPriorityScore(b.familyId, familyMastery)
    if (familyPriorityA !== familyPriorityB)
      return familyPriorityB - familyPriorityA

    // Then by difficulty (higher = more challenging, slight preference)
    return b.difficulty - a.difficulty
  })

  // Take top candidates (within the tight window, most are relevant)
  const candidatePool = prioritizedPool.slice(
    0,
    Math.max(5, Math.ceil(prioritizedPool.length * 0.7)),
  )

  // === SUBSKILL BALANCING ===
  const subSkills = Array.from(
    new Set(candidatePool.map((q) => q.subSkill).filter(Boolean) as string[]),
  )

  if (subSkills.length === 0) {
    const shuffled = shuffleArray(candidatePool)
    return getQuestionWithExplanation(shuffled[0].id)
  }

  subSkills.sort((a, b) => {
    const progA = subSkillProgress[a]
    const progB = subSkillProgress[b]
    return getPriorityScore(progB) - getPriorityScore(progA)
  })

  let targetSubSkill: string
  if (subSkills.length === 1 || Math.random() < 0.5) {
    targetSubSkill = subSkills[0]
  } else {
    const remaining = subSkills.slice(1)
    targetSubSkill = remaining[Math.floor(Math.random() * remaining.length)]
  }

  let finalCandidates = candidatePool.filter(
    (q) => q.subSkill === targetSubSkill,
  )

  if (finalCandidates.length === 0) {
    finalCandidates = candidatePool
  }

  const shuffled = shuffleArray(finalCandidates)
  return getQuestionWithExplanation(shuffled[0].id)
}

function getPriorityScore(progress?: SubSkillProgress): number {
  if (!progress || progress.questionsAnswered === 0) return 50

  if (progress.trend === 'struggling') return 100
  if (progress.masteryScore < CONFIG.STRUGGLE_THRESHOLD) return 90
  if (progress.masteryScore < 60) return 70
  if (progress.masteryScore < CONFIG.MASTERY_THRESHOLD) return 50

  const daysSincePractice =
    (Date.now() - progress.lastPracticed) / (1000 * 60 * 60 * 24)
  if (daysSincePractice > 7) return 60

  return 10
}

/**
 * Select a single adaptive question for a practice session.
 *
 * v8.1: Uses strict difficulty window around each subSkill's mastery position.
 * The selectedGroup is used only to resolve the starting level (anchor grade context).
 */
export function selectAdaptiveQuestion(
  moduleId: ModuleId,
  selectedGroup: GroupId,
  questionStates: Record<string, QuestionState>,
  subSkillProgress: Record<string, SubSkillProgress>,
  recentQuestionIds: string[] = [],
  sessionMasteredCount: number = 0,
  _sessionOverflowCount: number = 0,
  recentlyAnsweredIds: string[] = [],
  startingLevelId?: number,
): Question | null {
  const groupLevels = getLevelsByGroup(selectedGroup)
  const effectiveStartingLevel =
    startingLevelId || groupLevels[0]?.id || FULL_LADDER[0]?.id || 101

  return selectFromLadder(
    moduleId,
    questionStates,
    subSkillProgress,
    recentQuestionIds,
    sessionMasteredCount,
    recentlyAnsweredIds,
    effectiveStartingLevel,
  )
}

/**
 * Generate a practice session of `count` questions.
 *
 * v8.1: Questions are constrained to the difficulty window around mastery positions.
 *
 * INVARIANTS:
 * - No duplicate question IDs in the session
 * - At most one question per familyId (best effort)
 * - Deterministic retry limits prevent infinite loops
 * - All questions within the strict difficulty window
 */
export function getAdaptiveSession(
  moduleId: ModuleId,
  selectedGroup: GroupId,
  questionStates: Record<string, QuestionState>,
  subSkillProgress: Record<string, SubSkillProgress>,
  count: number = 10,
  recentlyAnsweredIds: string[] = [],
  startingLevelId?: number,
): Question[] {
  const session: Question[] = []
  const sessionQuestionIds: string[] = []
  const sessionFamilyIds: Set<string> = new Set()
  let masteredCount = 0
  let totalRetries = 0

  for (let i = 0; i < count; i++) {
    let slotRetries = 0
    let slotFilled = false

    while (
      slotRetries < CONFIG.MAX_RETRIES_PER_SLOT &&
      totalRetries < CONFIG.MAX_TOTAL_RETRIES
    ) {
      const q = selectAdaptiveQuestion(
        moduleId,
        selectedGroup,
        questionStates,
        subSkillProgress,
        sessionQuestionIds,
        masteredCount,
        0,
        recentlyAnsweredIds,
        startingLevelId,
      )

      if (!q) break

      // Check familyId uniqueness
      if (q.familyId && sessionFamilyIds.has(q.familyId)) {
        sessionQuestionIds.push(q.id)
        slotRetries++
        totalRetries++
        continue
      }

      // Valid question — add to session
      session.push(q)
      sessionQuestionIds.push(q.id)
      if (q.familyId) sessionFamilyIds.add(q.familyId)

      const state = questionStates[q.id]
      if (state?.status === 'mastered') {
        masteredCount++
      }

      slotFilled = true
      break
    }

    if (!slotFilled) {
      if (totalRetries >= CONFIG.MAX_TOTAL_RETRIES) {
        console.warn(
          `⚠️ Session generation: hit total retry limit (${CONFIG.MAX_TOTAL_RETRIES}). Generated ${session.length}/${count} questions.`,
        )
        break
      }
      break
    }
  }

  // Log session summary
  if (session.length > 0) {
    const levelCounts = new Map<string, number>()
    session.forEach((q) => {
      const label = getLevelById(q.difficulty)?.label || `${q.difficulty}`
      levelCounts.set(label, (levelCounts.get(label) || 0) + 1)
    })
    const summary = Array.from(levelCounts.entries())
      .map(([label, count]) => `${label}:${count}`)
      .join(', ')
    console.log(`📋 Session: ${session.length} questions [${summary}]`)
  }

  return session
}

export interface LevelProgressInfo {
  levels: LevelConfig[]
  currentLevelId: number
  levelDetails: {
    levelId: number
    label: string
    title: string
    isUnlocked: boolean
    isCurrent: boolean
    isCompleted: boolean
    totalQuestions: number
    attemptedCount: number
    practicedOrMasteredCount: number
    progressPercent: number
    group: number
  }[]
  fastTrackActive: boolean
}

/**
 * Compute level progress for display.
 *
 * v8.1: Shows progress for the student's current group levels plus a small
 * lookahead, rather than the entire ladder from starting level to E8+.
 */
export function computeLevelProgress(
  moduleId: ModuleId,
  group: GroupId,
  questionStates: Record<string, QuestionState>,
  startingLevelId?: number,
): LevelProgressInfo {
  const groupLevels = getLevelsByGroup(group)
  const effectiveStartingLevel = startingLevelId || groupLevels[0]?.id || 101

  // Show levels from the starting level through the end of the current group,
  // plus the first 2 levels of the next group as a lookahead
  const currentGroupEnd = groupLevels[groupLevels.length - 1]?.id || 999
  const nextGroupStart =
    group < 8 ? getLevelsByGroup((group + 1) as GroupId) : []
  const lookaheadIds = new Set(nextGroupStart.slice(0, 2).map((l) => l.id))

  const visibleLevels = FULL_LADDER.filter(
    (l) =>
      (l.id >= effectiveStartingLevel && l.id <= currentGroupEnd) ||
      lookaheadIds.has(l.id),
  )

  const unlockedBySubSkill = computeAllUnlockedLevels(
    moduleId,
    questionStates,
    effectiveStartingLevel,
  )

  const globalUnlocked = new Set<number>()
  unlockedBySubSkill.forEach((levels) => {
    levels.forEach((levelId) => globalUnlocked.add(levelId))
  })

  let fastTrackActive = false
  for (const level of visibleLevels) {
    if (detectFastTrack(moduleId, level.id, questionStates)) {
      fastTrackActive = true
      break
    }
  }

  const levelDetails = visibleLevels.map((level) => {
    const questionsAtLevel = ALL_QUESTIONS_CORE.filter(
      (q) => q.module === moduleId && q.difficulty === level.id,
    )

    let attemptedCount = 0
    let practicedOrMasteredCount = 0
    questionsAtLevel.forEach((q) => {
      const state = questionStates[q.id]
      if (state && state.attempts > 0) {
        attemptedCount++
        if (state.status === 'practiced' || state.status === 'mastered') {
          practicedOrMasteredCount++
        }
      }
    })

    const totalQuestions = questionsAtLevel.length
    const isUnlocked = globalUnlocked.has(level.id)
    const isCompleted =
      totalQuestions > 0 &&
      practicedOrMasteredCount >=
        Math.ceil(totalQuestions * CONFIG.LEVEL_UNLOCK_THRESHOLD) &&
      attemptedCount >= CONFIG.LEVEL_UNLOCK_MIN_ATTEMPTED

    const progressPercent =
      totalQuestions > 0
        ? Math.round((practicedOrMasteredCount / totalQuestions) * 100)
        : 0

    return {
      levelId: level.id,
      label: level.label,
      title: level.title,
      isUnlocked,
      isCurrent: false,
      isCompleted,
      totalQuestions,
      attemptedCount,
      practicedOrMasteredCount,
      progressPercent,
      group: level.group,
    }
  })

  // Determine current level: highest unlocked, non-completed level with questions
  let currentLevelId = effectiveStartingLevel

  for (let i = levelDetails.length - 1; i >= 0; i--) {
    const detail = levelDetails[i]
    if (detail.isUnlocked && !detail.isCompleted && detail.totalQuestions > 0) {
      currentLevelId = detail.levelId
      break
    }
  }

  // If all unlocked levels are completed, pick the highest unlocked level
  const allUnlockedCompleted = levelDetails
    .filter((d) => d.isUnlocked && d.totalQuestions > 0)
    .every((d) => d.isCompleted)
  if (allUnlockedCompleted) {
    for (let j = levelDetails.length - 1; j >= 0; j--) {
      if (levelDetails[j].isUnlocked) {
        currentLevelId = levelDetails[j].levelId
        break
      }
    }
  }

  levelDetails.forEach((d) => {
    d.isCurrent = d.levelId === currentLevelId
  })

  return {
    levels: visibleLevels,
    currentLevelId,
    levelDetails,
    fastTrackActive,
  }
}
